import { useEffect, useState } from "react";
import { usePublicSiteSettings } from "@/hooks/useSiteSettings";

function loadTrackingScripts(settings: Record<string, string>) {
  const gtmId = settings["gtm_container_id"];
  const pixelId = settings["meta_pixel_id"];
  const clarityId = settings["clarity_project_id"];
  const ga4Id = settings["ga4_measurement_id"];
  const googleAdsId = settings["google_ads_id"];
  const tiktokId = settings["tiktok_pixel_id"];
  const linkedinId = settings["linkedin_partner_id"];
  const pinterestId = settings["pinterest_tag_id"];
  const twitterId = settings["twitter_pixel_id"];
  const hotjarId = settings["hotjar_site_id"];
  const mixpanelToken = settings["mixpanel_token"];
  const plausibleDomain = settings["plausible_domain"];
  const intercomId = settings["intercom_app_id"];
  const crispId = settings["crisp_website_id"];
  const tawktoPropertyId = settings["tawkto_property_id"];
  const tawktoWidgetId = settings["tawkto_widget_id"] || "default";
  const gscVerification = settings["gsc_verification"];
  const bingVerification = settings["bing_verification"];
  const cookiebotId = settings["cookiebot_cbid"];
  const cookieyesId = settings["cookieyes_id"];
  const sentryDsn = settings["sentry_dsn"];

  // Google Tag Manager — skip if already loaded from index.html
  if (gtmId && !document.getElementById("gtm-script")) {
    const gtmScript = document.createElement("script");
    gtmScript.id = "gtm-script";
    gtmScript.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(gtmScript);
  }

  // Google Analytics 4 (standalone - only if GTM is not configured)
  if (ga4Id && !gtmId && !document.getElementById("ga4-script")) {
    const gtagScript = document.createElement("script");
    gtagScript.id = "ga4-script";
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
    document.head.appendChild(gtagScript);

    const gtagInit = document.createElement("script");
    gtagInit.id = "ga4-init";
    gtagInit.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${ga4Id}');
    `;
    document.head.appendChild(gtagInit);
  }

  // Google Ads Tag (uses gtag.js)
  if (googleAdsId && !document.getElementById("gads-script")) {
    if (!document.getElementById("ga4-script") && !gtmId) {
      const gtagScript = document.createElement("script");
      gtagScript.id = "gads-script";
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`;
      document.head.appendChild(gtagScript);
    }
    const gadsInit = document.createElement("script");
    gadsInit.id = "gads-init";
    gadsInit.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('config', '${googleAdsId}', { 'allow_enhanced_conversions': true });
    `;
    document.head.appendChild(gadsInit);
  }

  // Meta Pixel (with event_id for CAPI deduplication)
  if (pixelId && !document.getElementById("meta-pixel-script")) {
    const pageViewEventId = crypto.randomUUID();
    (window as any).__meta_pageview_event_id = pageViewEventId;

    const pixelScript = document.createElement("script");
    pixelScript.id = "meta-pixel-script";
    pixelScript.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView', {}, {eventID: '${pageViewEventId}'});
    `;
    document.head.appendChild(pixelScript);

    const noscript = document.createElement("noscript");
    noscript.id = "meta-pixel-noscript";
    const img = document.createElement("img");
    img.height = 1;
    img.width = 1;
    img.style.display = "none";
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.head.appendChild(noscript);
  }

  // TikTok Pixel
  if (tiktokId && !document.getElementById("tiktok-pixel-script")) {
    const s = document.createElement("script");
    s.id = "tiktok-pixel-script";
    s.innerHTML = `
      !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
      ttq.load('${tiktokId}');
      ttq.page();
      }(window,document,'ttq');
    `;
    document.head.appendChild(s);
  }

  // LinkedIn Insight Tag
  if (linkedinId && !document.getElementById("linkedin-insight-script")) {
    const s = document.createElement("script");
    s.id = "linkedin-insight-script";
    s.innerHTML = `
      _linkedin_partner_id="${linkedinId}";
      window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
      (function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}
      var s=document.getElementsByTagName("script")[0];
      var b=document.createElement("script");b.type="text/javascript";b.async=true;
      b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b,s);})(window.lintrk);
    `;
    document.head.appendChild(s);
  }

  // Pinterest Tag
  if (pinterestId && !document.getElementById("pinterest-tag-script")) {
    const s = document.createElement("script");
    s.id = "pinterest-tag-script";
    s.innerHTML = `
      !function(e){if(!window.pintrk){window.pintrk=function(){window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var n=window.pintrk;n.queue=[],n.version="3.0";var t=document.createElement("script");t.async=!0,t.src=e;var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");
      pintrk('load','${pinterestId}');
      pintrk('page');
    `;
    document.head.appendChild(s);
  }

  // X (Twitter) Pixel
  if (twitterId && !document.getElementById("twitter-pixel-script")) {
    const s = document.createElement("script");
    s.id = "twitter-pixel-script";
    s.innerHTML = `
      !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
      twq('config','${twitterId}');
    `;
    document.head.appendChild(s);
  }

  // Microsoft Clarity
  if (clarityId && !document.getElementById("clarity-script")) {
    const clarityScript = document.createElement("script");
    clarityScript.id = "clarity-script";
    clarityScript.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${clarityId}");
    `;
    document.head.appendChild(clarityScript);
  }

  // Hotjar
  if (hotjarId && !document.getElementById("hotjar-script")) {
    const s = document.createElement("script");
    s.id = "hotjar-script";
    s.innerHTML = `
      (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:${hotjarId},hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `;
    document.head.appendChild(s);
  }

  // Mixpanel
  if (mixpanelToken && !document.getElementById("mixpanel-script")) {
    const s = document.createElement("script");
    s.id = "mixpanel-script";
    s.innerHTML = `
      (function(f,b){if(!b.__SV){var e,g,i,h;window.mixpanel=b;b._i=[];b.init=function(e,f,c){function g(a,d){var b=d.split(".");2==b.length&&(a=a[b[0]],d=b[1]);a[d]=function(){a.push([d].concat(Array.prototype.slice.call(arguments,0)))}}var a=b;"undefined"!==typeof c?a=b[c]=[]:c="mixpanel";a.people=a.people||[];a.toString=function(a){var d="mixpanel";"mixpanel"!==c&&(d+="."+c);a||(d+=" (stub)");return d};a.people.toString=function(){return a.toString(1)+".people (stub)"};i="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");for(h=0;h<i.length;h++)g(a,i[h]);var j="set set_once union unset remove delete".split(" ");a.get_group=function(){function b(c){d[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));a.push([e,call2])}}for(var d={},e=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<j.length;c++)b(j[c]);return d};b._i.push([e,f,c])};b.__SV=1.2;e=f.createElement("script");e.type="text/javascript";e.async=!0;e.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:"file:"===f.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";g=f.getElementsByTagName("script")[0];g.parentNode.insertBefore(e,g)}})(document,window.mixpanel||[]);
      mixpanel.init('${mixpanelToken}',{track_pageview:true});
    `;
    document.head.appendChild(s);
  }

  // Plausible Analytics
  if (plausibleDomain && !document.getElementById("plausible-script")) {
    const s = document.createElement("script");
    s.id = "plausible-script";
    s.defer = true;
    s.setAttribute("data-domain", plausibleDomain);
    s.src = "https://plausible.io/js/script.js";
    document.head.appendChild(s);
  }

  // Intercom
  if (intercomId && !document.getElementById("intercom-script")) {
    const s = document.createElement("script");
    s.id = "intercom-script";
    s.innerHTML = `
      window.intercomSettings={api_base:"https://api-iam.intercom.io",app_id:"${intercomId}"};
      (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${intercomId}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};l();}})();
    `;
    document.head.appendChild(s);
  }

  // Crisp
  if (crispId && !document.getElementById("crisp-script")) {
    const s = document.createElement("script");
    s.id = "crisp-script";
    s.innerHTML = `
      window.$crisp=[];window.CRISP_WEBSITE_ID="${crispId}";
      (function(){var d=document;var s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
    `;
    document.head.appendChild(s);
  }

  // Tawk.to
  if (tawktoPropertyId && !document.getElementById("tawkto-script")) {
    const s = document.createElement("script");
    s.id = "tawkto-script";
    s.innerHTML = `
      var Tawk_API=Tawk_API||{},Tawk_LoadStart=new Date();
      (function(){var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
      s1.async=true;s1.src='https://embed.tawk.to/${tawktoPropertyId}/${tawktoWidgetId}';
      s1.charset='UTF-8';s1.setAttribute('crossorigin','*');
      s0.parentNode.insertBefore(s1,s0);})();
    `;
    document.head.appendChild(s);
  }

  // Google Search Console verification (no delay needed - just a meta tag)
  if (gscVerification && !document.getElementById("gsc-verification")) {
    const meta = document.createElement("meta");
    meta.id = "gsc-verification";
    meta.name = "google-site-verification";
    meta.content = gscVerification;
    document.head.appendChild(meta);
  }

  // Bing Webmaster verification
  if (bingVerification && !document.getElementById("bing-verification")) {
    const meta = document.createElement("meta");
    meta.id = "bing-verification";
    meta.name = "msvalidate.01";
    meta.content = bingVerification;
    document.head.appendChild(meta);
  }

  // Cookiebot
  if (cookiebotId && !document.getElementById("cookiebot-script")) {
    const s = document.createElement("script");
    s.id = "cookiebot-script";
    s.src = `https://consent.cookiebot.com/uc.js`;
    s.setAttribute("data-cbid", cookiebotId);
    s.setAttribute("data-blockingmode", "auto");
    s.type = "text/javascript";
    document.head.insertBefore(s, document.head.firstChild);
  }

  // CookieYes
  if (cookieyesId && !document.getElementById("cookieyes-script")) {
    const s = document.createElement("script");
    s.id = "cookieyes-script";
    s.src = `https://cdn-cookieyes.com/client_data/${cookieyesId}/script.js`;
    s.type = "text/javascript";
    document.head.appendChild(s);
  }

  // Sentry (dynamic import)
  if (sentryDsn && !document.getElementById("sentry-script")) {
    const s = document.createElement("script");
    s.id = "sentry-script";
    s.src = "https://browser.sentry-cdn.com/7.119.0/bundle.min.js";
    s.crossOrigin = "anonymous";
    s.onload = () => {
      (window as any).Sentry?.init({ dsn: sentryDsn, tracesSampleRate: 0.1 });
    };
    document.head.appendChild(s);
  }
}

export function TrackingScripts() {
  const [ready, setReady] = useState(false);

  // Delay the site_settings fetch until after LCP (~4s)
  useEffect(() => {
    const timeoutId = setTimeout(() => setReady(true), 4000);
    return () => clearTimeout(timeoutId);
  }, []);

  const { data: settings } = usePublicSiteSettings(ready);

  useEffect(() => {
    if (!settings) return;
    loadTrackingScripts(settings);
  }, [settings]);

  return null;
}
